import React, { useContext } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { gql } from "apollo-boost";
import { useQuery } from "@apollo/react-hooks";
import { LOCKED_STATUS_QUERY, convertToShortDate } from "utils";
import { BOOST_PRODUCT_SETTINGS_GQL } from "utils/mutations";
import { withQuery, MobileContext, Loading } from "components/common";
import useQueryParams from "utils/hooks/useQueryParams";
import { isUserRiskLocked } from "utils/utils";
import { toEmoji } from "utils/tomoEmoji";
import {
  Password,
  EmailAddress,
  Address,
  PhoneNumber,
  ManagePaymentMethods,
  BoostMembership,
  ManageTrackedAccounts,
  ManageStripePayments,
  BoostBilling,
} from "./common/Sections";
import { boostProductTierConfig } from '../Boost/consts';
import { TABS } from "./consts";

import { track } from "../../../utils/analytics";

import SettingsMobile from "./SettingsMobile";
import SettingsDesktop from "./SettingsDesktop";

const toAccountSettings = (roles, user) => {
  const sections = {};
  if (
    (roles.user_terminated_with_balance && !roles.user_with_credit_boost_enrolled)
    && !roles.admin
  ) {
    return sections;
  }

  // can role lock any section if we want;
  sections.password = {
    title: "Password",
    Component: Password,
  };

  sections.emailAddress = {
    title: "Email Address",
    summary: "Your email address is <bold>example@gmail.com</bold>",
    Component: EmailAddress,
    accessor: (data) => data.email || "unknown",
    successSummary: "Your email address is <bold>example@gmail.com</bold>",
  };

  let addressSpecific = '<bold>address</bold>';
  if (roles.credit_card || roles.user_terminated_with_balance) {
    addressSpecific = ` <bold>billing and shipping</bold> address`;
  }

  if (user.billing_address?.street) {
    sections.address = {
      title: "Address",
      summary: `Your${addressSpecific} is here.`,
      Component: Address,
    };
  }

  if (user.phone_number) {
    sections.phoneNumber = {
      title: "Phone Number",
      summary: "Your phone number is <bold>+1 843-330-7767</bold>",
      Component: PhoneNumber,
      accessor: (data) => data.phone_number || "unknown",
      successSummary: "Your phone number is <bold>+1 843-330-7767</bold>",
    };
  }

  return sections;
};

const toTomoCardSettings = (roles) => {
  const sections = {};

  if (!roles.user_terminated_with_balance && !roles.credit_card) {
    return {};
  }

  sections.managePaymentMethods = {
    title: "Manage Payment Methods",
    summary: "You currently have <bold>unknown</bold> linked account(s)",
    accessor: (data) => data.payment_methods.length || "no",
    Component: ManagePaymentMethods,
    successSummary: "Your linked accounts have been updated.",
    successTitle: "Accounts saved",
  };

  // this user only sees managePaymentMethod section;
  if (roles.user_terminated_with_balance && !roles.admin) {
    return sections;
  }

  /*
  TEMP REMOVE CARD SECTIONS
  - sections.manageCard
  - sections.creditLimit
  */
  // can role lock any section if we want;
  // sections.manageCard = {
  //   title: "Manage Card",
  //   summary: "Your card is currently <bold>active</bold>",
  //   Component: ManageCard,
  //   accessor: (data, additionalData) => {
  //     const labels = {
  //       active: "active",
  //       inactive: "inactive",
  //       pending: "pending",
  //       terminated: "terminated, a new one is being shipped to you",
  //       locked: "locked",
  //       unknown: "in limbo",
  //     };

  //     let labelKey;
  //     if (additionalData?.isRiskLocked) {
  //       labelKey = "locked";
  //     } else if (data.card && data.card.status) {
  //       labelKey = data.card.status.toLowerCase();
  //     } else {
  //       labelKey = "uknown";
  //     }
  //     return labels[labelKey];
  //   },
  //   canExpand: ({ isRiskLocked }) => { return !isRiskLocked; },
  // };

  // sections.creditLimit = {
  //   title: "Spending Power",
  //   summary: "Your current spending power is <bold>here</bold>",
  //   Component: CreditLimit,
  //   successSummary:
  //     "We've received your spending power increase request and will get back to you soon!",
  //   successTitle: "Request Received",
  //   accessor: (data) =>
  //     ((data.credit || {}).credit_limit
  //       ? `$${(data.credit || {}).credit_limit}`
  //       : "here"),
  // };

  // if (isRiskLocked) {
  //   sections.creditLimit.title = "Credit Limit";
  //   sections.creditLimit.summary = "Your current credit limit is <bold>here</bold>";
  // }

  /*
  TEMP REMOVE CARD SECTIONS
  END
  */

  return sections;
};

const toBoostSettings = (roles, boostData) => {
  const { amount, dueDate, productFreeTrial, productTerm, productTier } = boostData;
  const sections = {};

  const currentTierConfig = boostProductTierConfig[productTerm]?.[productTier];

  if (roles.user_with_credit_boost_enrolled) {
    sections.currentTier = {
      title: "Membership",
      summary: "Your Current Membership is <bold>Tier</bold>",
      Component: BoostMembership,
      accessor: () => <span className="align-middle">{toEmoji(currentTierConfig?.icon, 20)} {currentTierConfig?.name} Journey</span>,
    };

    if (amount && dueDate && !productFreeTrial) {
      const rate = (amount / 100).toFixed(2);
      const nextDue = convertToShortDate(new Date(dueDate * 1000).toISOString());
      sections.payments = {
        title: "Billing",
        summary: `<bold>rate</bold> (taxes included) ${currentTierConfig?.billingPeriod?.toLowerCase()}. Renews on ${nextDue}`,
        Component: BoostBilling,
        accessor: () => `$${rate}`,
      };
    }

    sections.manageTrackedAccounts = {
      title: "Manage Tracked Accounts",
      summary: "You currently have <bold>unknown</bold> linked account(s)",
      accessor: (data) => data.boost.accounts.reduce((acc, obj) => {
        acc += obj.accounts.length;
        return acc;
      }, 0) || "No",
      Component: ManageTrackedAccounts,
      successSummary: "Your Tracked accounts have been updated.",
      successTitle: "Accounts saved",
    };

    sections.manageStripePayments = {
      title: "Payment Method",
      summary: "Manage your TomoBoost payment method",
      Component: ManageStripePayments,
    };
  }
  if (roles.user_with_credit_boost_terminated) {
    sections.currentTier = {
      title: "Membership",
      summary: "Your Boost Membership <bold>Tier</bold> is paused",
      Component: BoostMembership,
      accessor: () => <span className="align-middle">{toEmoji(currentTierConfig?.icon, 20)} {currentTierConfig?.name} Journey</span>,
    };

    sections.manageStripePayments = {
      title: "Payment Method",
      summary: "Manage your TomoBoost payment method",
      Component: ManageStripePayments,
    };
  }

  return sections;
};

const generateSections = ({
  user,
  roles,
  isRiskLocked,
  boostData,
}) => {
  const tabSections = [
    { tab: TABS.PERSONAL_INFO, sections: toAccountSettings(roles, user) },
    { tab: TABS.CREDIT_CARD, sections: toTomoCardSettings(roles, isRiskLocked) },
    { tab: TABS.TOMO_BOOST, sections: toBoostSettings(roles, boostData) },
  ];

  // only return sections that actually have stuff.
  return tabSections
    .reduce((acc, tab) => {
      if (Object.keys(tab.sections).length > 0) {
        acc.tabs.push(tab.tab);
        acc.sections.push(tab.sections);
      }
      return acc;
    }, { tabs: [], sections: [] });
};

const Settings = ({ data: { user, getUserBankAccounts }, roles }) => {
  const { isMobile } = useContext(MobileContext);
  track("DASHBOARD_SETTINGS_PAGE_LOADED");
  const queryParams = useQueryParams();
  const routeSection = queryParams.get("section");

  const { data: userLockedStatus } = useQuery(LOCKED_STATUS_QUERY);
  const { data: { getUserProductStatus = {}, getUserBoostPayments = {} } = {}, loading } = useQuery(BOOST_PRODUCT_SETTINGS_GQL);
  const isRiskLocked = isUserRiskLocked(userLockedStatus?.userLockedStatus);

  // BOOST_PRODUCT_GQL needs to be done loading for settings tabs to work.
  if (loading) {
    return <Loading />;
  }

  const { tabs, sections } = generateSections({
    user,
    roles,
    isRiskLocked,
    boostData: { ...getUserProductStatus, ...getUserBoostPayments },
  });

  return isMobile ? (
    <SettingsMobile
      sections={sections}
      tabs={tabs}
      routeSection={routeSection}
      user={{ ...user, boost: { ...getUserProductStatus, accounts: getUserBankAccounts } }}
      roles={roles}
      isRiskLocked={isRiskLocked}
    />
  ) : (
    <SettingsDesktop
      sections={sections}
      tabs={tabs}
      routeSection={routeSection}
      user={{ ...user, boost: { ...getUserProductStatus, accounts: getUserBankAccounts } }}
      roles={roles}
      isRiskLocked={isRiskLocked}
    />
  );
};

const SETTINGS_QUERY = gql`
  query Settings {
    user {
      first_name
      last_name
      dob
      ssn_last_four
      phone_number
      email
      billing_address {
        street
        city
        state
        zip
      }
      shipping_address {
        street
        city
        state
        zip
      }
      coinbase_email
      card {
        status
        last_four_digits
        metadata
      }
      payment_methods {
        id
        nickname
        institution
        logo
        type
        account_number_last_four
        unlinked
        relink_id
        aggregator
        account_validation_needed
        payment_method_id
      }
      credit {
        credit_limit
        credit_used
        credit_available
        limit_increase {
          eligible
          reason
          last_request
          last_increase
        }
      }
      default_payment_method_id
      secondary_payment_method_id
      autopay_frequency
      smart_pay
      api_type
      aggregator_type
    },
    getUserBankAccounts {
      institutionName
      logoURL
      accounts {
        uuid
        mask
        name
        type
        appliedProducts
        statusCodeUpdatedAt
      }
    }
  }
`;

const mapStateToProps = (state) => ({
  roles: state.user.roles,
});

export default compose(
  connect(mapStateToProps),
  withQuery(SETTINGS_QUERY),
)(Settings);
